'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal, ModalBody, ModalHeader,
} from 'reactstrap';
import { Button } from 'reactstrap';
import moment from 'moment';

import ListingComponent from 'src/components/shared/listing-component';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import { updateUrlForOpenJobSidePanel } from 'src/helpers/job-slide-out';
import { getMaintenanceHistory } from 'src/redux/jobs/api';

interface MaintenanceHistoryData {
  count?: number;
  address?: string;
  jobs?: Record<string, unknown>[];
  [key: string]: unknown;
}

const mrStatus: Record<string, string> = {
  created: 'awaiting-availability',
  scheduling_in_progress: 'scheduling',
  scheduled: 'scheduled',
  quoting: 'paused',
  quality_check: 'paused',
  paused: 'paused',
  invoice_ready: 'in-progress',
  work_in_progress: 'in-progress',
  closed: 'in-progress',
  work_completed: 'completed',
  canceled: 'canceled',
};

const MaintenanceHistoryModal = () => {
  const dispatch = useAppDispatch();
  const { extraProps } = useAppSelector((state: Record<string, unknown>) => {
    const modals = state?.modals as Record<string, unknown>;
    return modals?.maintenanceHistoryModal as Record<string, unknown>;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [sort, setSort] = useState('');
  const [maintenanceHistory, setMaintenanceHistory] = useState<MaintenanceHistoryData>({});

  const handleCloseModal = useCallback(() => {
    dispatch(updateVisibility({ modalType: 'maintenanceHistoryModal', isVisible: false }));
  }, [dispatch]);

  const ep = extraProps as Record<string, unknown> | undefined;

  useEffect(() => {
    setIsLoading(true);
    dispatch(getMaintenanceHistory(ep?.serviceRequestNo as string)(sort ? { sort } : { }))
      .unwrap()
      .then((res: unknown) => { setMaintenanceHistory(res as MaintenanceHistoryData); })
      .catch(() => setMaintenanceHistory({}))
      .finally(() => setIsLoading(false));
  }, [dispatch, sort, ep?.serviceRequestNo]);

  return (
    <Modal isOpen centered className="maintenance-history-modal">
      <ModalHeader toggle={handleCloseModal}>
        Maintenance History
        {' '}
        {maintenanceHistory?.count ? `(${maintenanceHistory?.count})` : ''}
      </ModalHeader>
      <ModalBody>
        {maintenanceHistory?.address ? (
          <div className="address-box">
            Address:
            <span>{maintenanceHistory?.address}</span>
          </div>
        ) : ''}
        <ListingComponent
          isLoading={isLoading}
          mappings={[
            {
              key: 'service_request_number',
              label: 'Lula MR #',
              render: (item: Record<string, unknown>) => (
                <React.Fragment>
                  <Button
                    type="button"
                    className="btn-link clickable"
                    color="none"
                    onClick={() => {
                      updateUrlForOpenJobSidePanel(
                        item?.service_request_number as string,
                        'serviceRequestDetails',
                        '',
                        item?.service_request_id as string,
                      );
                      handleCloseModal();
                    }}
                  >
                    {item?.service_request_number as string}
                  </Button>
                  <p>
                    {item?.jobs_count as string | number}
                    {' '}
                    Jobs
                  </p>
                </React.Fragment>
              ),
            },
            {
              key: 'external_job_source',
              label: 'PM WO #',
              render: (item: Record<string, unknown>) => {
                const externalJobSource = (
                  item?.external_job_source
                ) as Record<
                  string, unknown
                > | undefined;
                return (
                  <React.Fragment>
                    {externalJobSource?.reference_no ? (
                      <Button
                        type="button"
                        className={`btn-link ${externalJobSource?.link ? 'clickable' : ''}`}
                        color="none"
                        onClick={() => {
                          if (externalJobSource?.link) {
                            window.open(externalJobSource?.link as string, '_blank');
                          }
                        }}
                      >
                        {externalJobSource?.reference_no as string}
                      </Button>
                    ) : '-'}
                    {externalJobSource?.label
                      ? <p>{externalJobSource?.label as string}</p> : ''}
                  </React.Fragment>
                );
              },
            },
            {
              key: 'service_category',
              label: 'Service Vertical',
              render: (item: Record<string, unknown>) => (
                <p>
                  <span>{item?.service_category as string}</span>
                </p>
              ),
            },
            {
              key: 'status_slug',
              label: 'STATUS',
              sortable: true,
              sortKey: 'status',
              render: (item: Record<string, unknown>) => (
                <div className={
                  `status ${mrStatus?.[
                    item?.status_slug as string
                  ]}`
                }
                >
                  {item?.status as string}
                </div>
              ),
            },
            {
              key: 'cost',
              label: 'COST',
              sortable: true,
              sortKey: 'cost',
              render: (
                item: Record<string, unknown>,
              ) => `$${
                (item?.cost as string | number)
                || 0.00
              }`,
            },
            {
              key: 'created_at',
              label: 'SUBMITTED DATE',
              render: (
                item: Record<string, unknown>,
              ) => (
                (item?.created_at as string)
                  && (item?.timezone as string)
                  ? moment?.utc(
                    item?.created_at as string,
                  )?.tz(
                    item?.timezone as string,
                  )?.format(
                    'hh:mma MMM DD, YYYY z',
                  )
                  : ''
              ),
            },
          ]}
          tableWrapperClassName="table-wrapper"
          className="maintenance-history-table"
          primaryKey="service_request_no"
          dataSource={maintenanceHistory?.jobs || []}
          onSortChange={(sortValue: string) => setSort(sortValue)}
          tableResponsive={false}
        />
      </ModalBody>
    </Modal>
  );
};

export default MaintenanceHistoryModal;
